import { Form, Formik, useFormikContext } from "formik";
import React, { useEffect, useRef, useState } from "react";
import AddButton from "../buttons/button";
import moment from "moment";
import errorToast from "../helper-components/error-toast";
import { useDispatch } from "react-redux";
import { purchaseTaxCalAction } from "../../redux/actions/purchase-action";
import * as Yup from "yup";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import CloseIcon from "../../assets/icons/close-icon";
import InputField from "../forms/input-field";
import SelectField from "../forms/select-field";
import { ChevronDownIcon, PlusIcon, SquarePlus } from "lucide-react";
import { Package } from "lucide-react";

import {
  decimalNumberFn,
  minDate,
  positiveIntegerFn,
} from "../helper-components/helper-function";
import LoadingOverlay from "../helper-components/overlay-loading";

const PurchaseBatchesPanel = ({
  setOpen,
  itemId,
  binLocationList = [],
  disabled = false,
  itemValues = {},
  open,
  prefix = "gort",
  searchRef,
}) => {
  const batchRef = useRef();
  const [loading, setLoading] = useState(false);
  const [openBatchIndex, setOpenBatchIndex] = useState(null);
  const defaultSelectedItem = itemValues?.data?.find(
    (item, index) => itemId === item?.item_id
  );

  const [newBatch, setNewBatch] = useState(
    defaultSelectedItem?.item_batches?.length > 0 ? false : true
  );

  useEffect(() => {
    if (open) {
      setNewBatch(defaultSelectedItem?.item_batches?.length > 0 ? false : true);
      batchRef.current?.focus();
    }
  }, [open]);

  const { setFieldValue } = useFormikContext();
  const batches = defaultSelectedItem?.item_batches;
  const dispatch = useDispatch();
  const defaultSelectedItemIndex = itemValues?.data?.findIndex(
    (item, index) => itemId === item?.item_id
  );
  const validationSchema = Yup.object().shape({
    // item_batches: Yup.array().of(
    // Yup.object().shape({
    item_batch_purchase_rate: Yup.number()
      .typeError("Must be a number")
      .required("Purchase rate is required")
      .moreThan(0, "Purchase rate must be greater than 0"),

    item_batch_unit_price: Yup.number()
      .typeError("Must be a number")
      .required("MRP is required")
      .moreThan(0, "MRP must be greater than 0")
      .test(
        "MRP-greater-than-final-purchase-rate",
        "MRP must be greater than the final purchase rate",
        function (value) {
          const { item_batch_final_purchase_rate } = this?.parent;
          return value > item_batch_final_purchase_rate;
        }
      ),
    item_sellable_quantity: Yup.number()
      .typeError("Must be a number")
      .integer("Quantity must be an integer")
      .required("Sellable quantity is required")
      .moreThan(0, "Sellable quantity must be greater than 0"),
    item_batch_free_quantity: Yup.number()
      .transform((value) =>
        typeof value === "string" ? parseInt(value, 10) : value
      ) // Transform string to number
      .typeError("Must be a number")
      .integer("Quantity must be an integer"),
    item_batch_number: Yup.string().required("Batch number is required"),
    // to_bin_id: Yup.string().required("Bin ID is required"),
    item_exp_date: Yup.date().required("Expiration date is required"),
    // })
    // ),
  });
  const today = new Date();
  const sixMonthsLater = new Date(today.setMonth(today.getMonth() + 6));
  const initialValues =
    {
      item_id: defaultSelectedItem?.item_id,
      item_batch_number: "",
      item_sellable_quantity: "",
      item_non_sellable_quantity: "",
      item_batch_free_quantity: "",
      item_batch_total_effective_qauntity: 0, //new
      item_batch_purchase_rate: "",
      item_batch_total_base_purchase_rate: 0, //new
      item_batch_for_qauntity: "", //new
      item_batch_for_free_qauntity: "", //new
      item_batch_scheme_discount_percentage: "", //new
      item_batch_scheme_discount_amount: 0, //new
      item_batch_scheme_purchase_rate: 0, //new
      item_batch_discount_percentage: "",
      item_batch_gen_discount_amount: 0, //new
      item_batch_discounted_purchase_rate: "", //new
      item_batch_discount_amount: 0,
      item_batch_tax_percentage: "",
      item_batch_tax_amount: 0,
      item_batch_final_purchase_rate: 0,
      item_batch_unit_price: "",
      item_batch_total_purchase_rate: 0,
      item_batch_margin_percentage: 0,
      item_exp_date: moment(sixMonthsLater).format("YYYY-MM-DD"), // Correct format
      item_mfg_date: "2023/12/02",
      from_bin_location: "",
      to_bin_location: "receiving-bin",
      to_bin_id: binLocationList?.find(
        (bin) => bin?.actualLabel === "receiving-bin"
      )?.value,
      from_bin_id: "",
      // item_batches: [],
    } ?? {};

  const handleQuantityChange = async (
    qty,
    pr = 0,
    dis = 0,
    item_batch_for_qauntity = 0,
    item_batch_for_free_qauntity = 0,
    item_batch_free_quantity = 0,
    setValues,
    values,
    schemePer = 0,
    item_batch_unit_price
  ) => {
    let updatedData = { ...values };
    const denominator =
      parseInt(item_batch_for_qauntity) +
      parseInt(item_batch_for_free_qauntity);
    const item_batch_scheme_discount_percentage = schemePer
      ? schemePer
      : denominator > 0
      ? parseFloat(
          (parseInt(item_batch_for_free_qauntity) / denominator) * 100
        ).toFixed(2)
      : 0;
    const item_batch_total_base_purchase_rate =
      parseFloat(pr || 0) * parseInt(qty || 0);

    const item_batch_scheme_discount_amount =
      parseFloat(
        (item_batch_scheme_discount_percentage / 100) *
          item_batch_total_base_purchase_rate
      ).toFixed(2) || 0;
    const item_batch_scheme_purchase_rate =
      (
        parseFloat(item_batch_total_base_purchase_rate || 0) -
        parseFloat(item_batch_scheme_discount_amount || 0)
      ).toFixed(2) || 0;

    const item_batch_gen_discount_amount =
      parseFloat(dis * item_batch_scheme_purchase_rate).toFixed(2) ?? 0;

    const item_batch_discounted_purchase_rate =
      parseFloat(
        item_batch_scheme_purchase_rate - item_batch_gen_discount_amount
      )?.toFixed(2) || 0;

    const item_batch_total_effective_qauntity =
      parseInt(qty) + parseInt(item_batch_free_quantity);
    setLoading(true);
    dispatch(
      purchaseTaxCalAction(
        {
          item_id: defaultSelectedItem?.item_id,
          purchase_rate:
            item_batch_scheme_purchase_rate -
            item_batch_scheme_purchase_rate * (dis || 0),
        },
        (data) => {
          setLoading(false);
          const {
            item_discount_amount,
            item_tax_amount,
            item_total_tax_percentage,
          } = data.data;

          const discount = item_discount_amount || 0;
          const tax = +item_tax_amount || 0;
          const sgst = +(tax / 2)?.toFixed(2);
          const cgst = +(tax / 2)?.toFixed(2);
          const igst = 0;
          const item_batch_total_purchase_rate = parseFloat(
            parseFloat(item_batch_discounted_purchase_rate) + parseFloat(tax)
          ).toFixed(2);
          const item_batch_final_purchase_rate = parseFloat(
            item_batch_total_purchase_rate / item_batch_total_effective_qauntity
          ).toFixed(2);

          const item_batch_margin_percentage =
            (
              ((parseFloat(item_batch_unit_price) -
                parseFloat(item_batch_final_purchase_rate)) /
                parseFloat(item_batch_final_purchase_rate)) *
              100
            ).toFixed(2) || 0;

          const updatedDetails = {
            ...updatedData,
            item_sellable_quantity: +qty || "",
            item_batch_free_quantity: +item_batch_free_quantity || "",
            item_batch_total_effective_qauntity:
              item_batch_total_effective_qauntity,
            item_batch_purchase_rate: pr || "",
            item_batch_total_base_purchase_rate:
              item_batch_total_base_purchase_rate,
            item_batch_for_qauntity: item_batch_for_qauntity || "",
            item_batch_for_free_qauntity: item_batch_for_free_qauntity || "",
            item_batch_scheme_discount_percentage:
              item_batch_scheme_discount_percentage || "",
            item_batch_scheme_discount_amount:
              item_batch_scheme_discount_amount || 0,
            item_batch_scheme_purchase_rate:
              item_batch_scheme_purchase_rate || "",
            item_batch_discount_percentage: dis * 100 || "",
            item_batch_gen_discount_amount: item_batch_gen_discount_amount,
            item_batch_discounted_purchase_rate:
              item_batch_discounted_purchase_rate || 0,
            item_batch_discount_amount: (
              parseFloat(item_batch_gen_discount_amount) +
              parseFloat(item_batch_scheme_discount_amount)
            ).toFixed(2),
            item_batch_tax_percentage: item_total_tax_percentage,
            item_batch_tax_amount: parseFloat(tax).toFixed(2),
            item_batch_total_purchase_rate: item_batch_total_purchase_rate,
            item_batch_unit_price: item_batch_unit_price || "",
            item_batch_final_purchase_rate: item_batch_final_purchase_rate || 0,
            item_batch_margin_percentage: item_batch_margin_percentage,
            item_batch_sgst: +sgst.toFixed(2),
            item_batch_cgst: +cgst.toFixed(2),
            item_batch_igst: +igst,
          };

          setValues(updatedDetails);
        },
        (data) => {
          setLoading(false);
        }
      )
    );
  };

  const handleShowErrorToast = async (item_id = "", errors, values) => {
    // If values exist, check for batch field errors directly (not as an array)
    if (values) {
      const batchError = errors;

      let hasError = false;

      if (batchError?.item_batch_purchase_rate && !hasError) {
        errorToast(batchError.item_batch_purchase_rate);
        hasError = true;
      } else if (batchError?.item_batch_unit_price && !hasError) {
        errorToast(batchError.item_batch_unit_price);
        hasError = true;
      } else if (batchError?.item_sellable_quantity && !hasError) {
        errorToast(batchError.item_sellable_quantity);
        hasError = true;
      } else if (batchError?.item_batch_free_quantity && !hasError) {
        errorToast(batchError.item_batch_free_quantity);
        hasError = true;
      } else if (batchError?.item_batch_number && !hasError) {
        errorToast(batchError.item_batch_number);
        hasError = true;
      } else if (batchError?.item_exp_date && !hasError) {
        errorToast(batchError.item_exp_date);
        hasError = true;
      }
    } else {
    }
  };

  const handleDeleteBatch = (batchIndex) => {
    const updatedBatches = [...(batches || [])];
    updatedBatches.splice(batchIndex, 1);

    const sumQuantity = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_sellable_quantity) || 0),
      0
    );
    const totalDiscount = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_discount_amount) || 0),
      0
    );
    const totalTax = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_tax_amount) || 0),
      0
    );
    const totalAmt = updatedBatches.reduce(
      (sum, batch) =>
        sum + (parseFloat(batch.item_batch_total_purchase_rate) || 0),
      0
    );
    const totalFreeQty = updatedBatches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_free_quantity) || 0),
      0
    );

    const updatedDataRow = {
      ...defaultSelectedItem,
      item_batches: updatedBatches,
      quantity: sumQuantity,
      item_free_quantity: +totalFreeQty,
      gort_total_discount: +totalDiscount,
      item_discount_amount: +totalDiscount,
      item_tax_amount: +totalTax?.toFixed(2),
      // item_discount_percentage: +item_discount_percentage,
      item_gst: updatedBatches?.[0]?.item_total_tax_percentage || 0,
      item_sgst: +(totalTax / 2)?.toFixed(2),
      item_cgst: +(totalTax / 2)?.toFixed(2),
      item_igst: 0,
      item_pack_size: defaultSelectedItem?.base_quantity * sumQuantity,
      item_total_amount: totalAmt.toFixed(2),
    };
    setFieldValue(`data.${defaultSelectedItemIndex}`, updatedDataRow);
    const updatedData = [...itemValues.data];
    updatedData[defaultSelectedItemIndex] = updatedDataRow;

    // Inline total, tax, discount calculations
    const raw_total_amount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_total_amount) || 0);
    }, 0);

    const total_tax = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_tax_amount) || 0);
    }, 0);

    const total_discount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_discount_amount) || 0);
    }, 0);

    // Subtotal = Total - Tax - Discount
    const sub_total = (raw_total_amount - total_tax + total_discount).toFixed(
      2
    );

    const total_amount = Math.round(raw_total_amount).toFixed(2);
    const round_off_value = (
      Math.round(raw_total_amount) - raw_total_amount
    ).toFixed(2);

    // Set all values to form
    setFieldValue(`${prefix}_sub_total`, sub_total);
    setFieldValue(`${prefix}_total_gst`, total_tax);
    setFieldValue(`${prefix}_total_discount`, total_discount);
    setFieldValue(`${prefix}_total_amount`, total_amount);
    setFieldValue(`${prefix}_round_off_value`, round_off_value);
    setFieldValue(`${prefix}_tcs_or_tds_amount`, 0);
    setFieldValue(`${prefix}_tcs_or_tds_percentage_id`, "");
    setFieldValue(`${prefix}_tcs_or_tds_percentage`, 0);
    setFieldValue(`${prefix}_tds_tcs_type`, "");

    setNewBatch(false);
    setOpenBatchIndex(null);
  };

  const innerOnSubmit = (values) => {
    // Make a copy of current batches array or empty array if undefined
    const item_batches = [...(batches || [])];

    if (
      typeof openBatchIndex === "number" &&
      openBatchIndex >= 0 &&
      openBatchIndex < item_batches.length
    ) {
      // Update existing batch at openBatchIndex
      item_batches[openBatchIndex] = values;
    } else {
      // Add new batch
      item_batches.push(values);
    }

    // Now compute all totals based on updated item_batches
    const sumQuantity = item_batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_sellable_quantity) || 0),
      0
    );
    const totalDiscount = item_batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_discount_amount) || 0),
      0
    );
    const totalTax = item_batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_tax_amount) || 0),
      0
    );
    const totalAmt = item_batches.reduce(
      (sum, batch) =>
        sum + (parseFloat(batch.item_batch_total_purchase_rate) || 0),
      0
    );
    const totalFreeQty = item_batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.item_batch_free_quantity) || 0),
      0
    );

    const updatedDataRow = {
      ...defaultSelectedItem,
      item_batches,
      quantity: sumQuantity,
      item_free_quantity: +totalFreeQty,
      gort_total_discount: +totalDiscount,
      item_discount_amount: +totalDiscount,
      item_tax_amount: +totalTax?.toFixed(2),
      item_gst: item_batches?.[0]?.item_batch_tax_percentage || 0,
      item_sgst: +(totalTax / 2)?.toFixed(2),
      item_cgst: +(totalTax / 2)?.toFixed(2),
      item_igst: 0,
      item_pack_size: defaultSelectedItem?.base_quantity * sumQuantity,
      item_total_amount: totalAmt.toFixed(2),
    };

    // Update the main data array
    setFieldValue(`data.${defaultSelectedItemIndex}`, updatedDataRow);

    const updatedData = [...itemValues.data];
    updatedData[defaultSelectedItemIndex] = updatedDataRow;

    // Inline total, tax, discount calculations
    const raw_total_amount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_total_amount) || 0);
    }, 0);

    const total_tax = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_tax_amount) || 0);
    }, 0);

    const total_discount = updatedData.reduce((sum, item) => {
      return sum + (parseFloat(item.item_discount_amount) || 0);
    }, 0);

    // Subtotal = Total - Tax - Discount
    const sub_total = (raw_total_amount - total_tax + total_discount).toFixed(
      2
    );
    const total_amount = Math.round(raw_total_amount).toFixed(2);
    const round_off_value = (
      Math.round(raw_total_amount) - raw_total_amount
    ).toFixed(2);

    // Clear TCS/TDS

    // Set all values to form
    setFieldValue(`${prefix}_sub_total`, sub_total);
    setFieldValue(`${prefix}_total_gst`, total_tax);
    setFieldValue(`${prefix}_total_discount`, total_discount);
    setFieldValue(`${prefix}_total_amount`, total_amount);
    setFieldValue(`${prefix}_round_off_value`, round_off_value);
    setFieldValue(`${prefix}_tcs_or_tds_amount`, 0);
    setFieldValue(`${prefix}_tcs_or_tds_percentage_id`, "");
    setFieldValue(`${prefix}_tcs_or_tds_percentage`, 0);
    setFieldValue(`${prefix}_tds_tcs_type`, "");

    setNewBatch(false);
    setOpenBatchIndex(null);
  };

  const defaultInputClass =
    "min-h-[42px] rounded-[8px] px-2 bg-[var(--vert-input-bg)] text-[var(--vert-input-color)] text-[length:var(--vert-input-font-size)] font-[var(--vert-input-font-weight)] rounded input-field-no-spin-buttons placeholder:text-[var(--vert-input-place-color)] placeholder:text-[length:var(--vert-input-place-font-size)] placeholder:font-[var(--vert-input-place-font-weight)] focus:outline-none focus:border-[var(--vert-input-focus)] focus:text-[var(--vert-input-color)] focus:bg-[var(--vert-input-bg)]";
  const labelClass =
    "whitespace-nowrap flex text-[var(--vert-input-label-color)] text-[length:var(--vert-input-label-font-size)] font-[var(--vert-input-label-font-weight)]";
  const errorBorder = "border border-[var(--vert-input-error-border)]";
  const inputBorder = "border border-[var(--vert-input-border)]";
  const toggleBatch = (index) => {
    setOpenBatchIndex((prev) => (prev === index ? null : index));
  };

  const expiryRef = useRef();
  const binRef = useRef();
  const freeQtyRef = useRef();
  const mrpRef = useRef();
  const basePrRef = useRef();
  const schemeQtyRef = useRef();
  const schemeFreeQtyRef = useRef();
  const schemePerRef = useRef();
  const DisPerRef = useRef();

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          setOpen(false);
        }}
      >
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl">
                {/* Header */}

                <Formik
                  enableReinitialize={true}
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={innerOnSubmit}
                >
                  {({
                    values,
                    handleSubmit,
                    errors,
                    setFieldValue,
                    setValues,
                  }) => {
                    return (
                      <Form className="h-full relative">
                        <div
                          className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b"
                          style={{ borderColor: "var(--slide-panel-border)" }}
                        >
                          <div
                            style={{
                              maxHeight: "32px",
                              color: "var(--slide-panel-h-fg)",
                              fontSize: "var(--slide-panel-h-fs)",
                              fontWeight: "var(--slide-panel-h-fw)",
                            }}
                            className="min-w-[75%] "
                          >
                            {newBatch && openBatchIndex
                              ? "Update Batch"
                              : newBatch
                              ? "Add New Batch"
                              : ``}
                          </div>
                          <AddButton
                            onclkFn={() => {
                              setOpen(false);
                            }}
                            icon={
                              <CloseIcon
                                width="12px"
                                height="12px"
                                fill="var(--close-button-fg)"
                              />
                            }
                            text={
                              <span>
                                Close <span className="opacity-50">(X)</span>{" "}
                              </span>
                            }
                            className="flex h-[32px]  items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                          />
                        </div>
                        {newBatch ? (
                          <div
                            style={{
                              height: `calc(100% - 9rem)`,
                              minHeight: `calc(100% - 9rem)`,
                            }}
                            className="px-4 pt-5 relative"
                          >
                            <div className="h-full w-full flex flex-col gap-2  overflow-y-auto justify-between">
                              <div className="flex flex-col gap-2">
                                <InputField
                                  maxLength={20}
                                  autoFocus={true}
                                  inputRef={batchRef}
                                  defaultInputClass={defaultInputClass}
                                  labelClass={labelClass}
                                  errorBorder={errorBorder}
                                  inputBorder={inputBorder}
                                  isRequired={true}
                                  containerClass="w-[100%]"
                                  parentClass="flex flex-col gap-2"
                                  isDisabled={false}
                                  type="text"
                                  name="item_batch_number"
                                  placeholder="Batch No."
                                  label="Batch No."
                                  labelWidth="6.5rem"
                                  errMsgContainerClass="hidden"
                                  onKeyDown={(e) => {
                                    if (e.key === "Tab" || e.key === "Enter") {
                                      e.preventDefault();
                                      expiryRef.current?.focus();
                                    }
                                  }}
                                />

                                <div className="flex gap-5">
                                  <InputField
                                    inputRef={expiryRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="date"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_exp_date"
                                    placeholder="Expiry Date"
                                    label="Expiry Date"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    min={minDate}
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        binRef.current?.focus();
                                      }
                                    }}
                                  />
                                  <SelectField
                                    ref={binRef}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    isDisabled={disabled}
                                    placeholder="Select Bin"
                                    options={binLocationList}
                                    label="To Bin"
                                    id={`to_bin_id`}
                                    name={`to_bin_id`}
                                    isSearchable={true}
                                    isRequired={false}
                                    defaultValue={{
                                      label:
                                        binLocationList?.find(
                                          (bin) =>
                                            bin?.value === values?.to_bin_id
                                        )?.label ?? "",
                                      value: "",
                                    }}
                                    handleSelected={(selectedoption) => {
                                      setFieldValue(
                                        `to_bin_location`,
                                        selectedoption?.actualLabel
                                      );
                                    }}
                                    errMsgContainerClass="hidden"
                                  />
                                </div>

                                <div className="flex gap-5">
                                  <InputField
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_sellable_quantity"
                                    placeholder="Purchase Qty"
                                    label="Purchase Qty"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onBlur={(e) => {
                                      const value = +(
                                        e.currentTarget.value || 0
                                      );
                                      handleQuantityChange(
                                        value,
                                        +values?.item_batch_purchase_rate || 0,
                                        parseFloat(
                                          values?.item_batch_discount_percentage ||
                                            0
                                        ) / 100,
                                        parseInt(
                                          values?.item_batch_for_qauntity || 0
                                        ),
                                        parseInt(
                                          values?.item_batch_for_free_qauntity ||
                                            0
                                        ),
                                        parseInt(
                                          values?.item_batch_free_quantity || 0
                                        ),
                                        setValues,
                                        values,
                                        parseFloat(
                                          values?.item_batch_scheme_discount_percentage ||
                                            0
                                        ),
                                        parseFloat(
                                          values?.item_batch_unit_price || 0
                                        )
                                      );
                                    }}
                                    onKeyDown={(e) => {
                                      positiveIntegerFn(e);
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        // const value = +(
                                        //   e.currentTarget.value || 0
                                        // );
                                        // handleQuantityChange(
                                        //   value,
                                        //   +values?.item_batch_purchase_rate ||
                                        //     0,
                                        //   parseFloat(
                                        //     values?.item_batch_discount_percentage ||
                                        //       0
                                        //   ) / 100,
                                        //   parseInt(
                                        //     values?.item_batch_for_qauntity || 0
                                        //   ),
                                        //   parseInt(
                                        //     values?.item_batch_for_free_qauntity ||
                                        //       0
                                        //   ),
                                        //   parseInt(
                                        //     values?.item_batch_free_quantity ||
                                        //       0
                                        //   ),
                                        //   setValues,
                                        //   values,
                                        //   parseFloat(
                                        //     values?.item_batch_scheme_discount_percentage ||
                                        //       0
                                        //   ),
                                        //   parseFloat(
                                        //     values?.item_batch_unit_price || 0
                                        //   )
                                        // );
                                        freeQtyRef.current?.focus();
                                      }
                                    }}
                                  />
                                  <InputField
                                    inputRef={freeQtyRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={false}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_batch_free_quantity"
                                    placeholder="Free Qty"
                                    label="Free Qty"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    handleChange={(e) => {}}
                                    onKeyDown={(e) => {
                                      positiveIntegerFn(e);
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        const value = +(
                                          e.currentTarget.value || 0
                                        );
                                        // handleQuantityChange(
                                        //   values?.item_sellable_quantity || 0,
                                        //   +values?.item_batch_purchase_rate ||
                                        //     0,
                                        //   parseFloat(
                                        //     values?.item_batch_discount_percentage ||
                                        //       0
                                        //   ) / 100,
                                        //   parseInt(
                                        //     values?.item_batch_for_qauntity || 0
                                        //   ),
                                        //   parseInt(
                                        //     values?.item_batch_for_free_qauntity ||
                                        //       0
                                        //   ),
                                        //   +value || 0,
                                        //   setValues,
                                        //   values,
                                        //   parseFloat(
                                        //     values?.item_batch_scheme_discount_percentage
                                        //   ),
                                        //   parseFloat(
                                        //     values?.item_batch_unit_price
                                        //   )
                                        // );
                                        mrpRef.current?.focus();
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = +(
                                        e.currentTarget.value || 0
                                      );
                                      handleQuantityChange(
                                        values?.item_sellable_quantity || 0,
                                        +values?.item_batch_purchase_rate || 0,
                                        parseFloat(
                                          values?.item_batch_discount_percentage ||
                                            0
                                        ) / 100,
                                        parseInt(
                                          values?.item_batch_for_qauntity || 0
                                        ),
                                        parseInt(
                                          values?.item_batch_for_free_qauntity ||
                                            0
                                        ),
                                        +value || 0,
                                        setValues,
                                        values,
                                        parseFloat(
                                          values?.item_batch_scheme_discount_percentage
                                        ),
                                        parseFloat(
                                          values?.item_batch_unit_price
                                        )
                                      );
                                      mrpRef.current?.focus();
                                    }}
                                  />
                                </div>
                                <div className="flex gap-5">
                                  <InputField
                                    inputRef={mrpRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_batch_unit_price"
                                    placeholder="MRP"
                                    label="MRP (Per 1 Qty)"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    handleChange={(e) => {}}
                                    // onKeyDown={decimalNumberFn}
                                    onKeyDown={(e) => {
                                      decimalNumberFn(e);
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        const value =
                                          e.currentTarget.value || 0;

                                        basePrRef.current?.focus();
                                      }
                                    }}
                                  />
                                  <InputField
                                    inputRef={basePrRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_batch_purchase_rate"
                                    placeholder="Base PR"
                                    label="Base PR (Per 1 Qty)"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    handleChange={(e) => {}}
                                    // onKeyDown={decimalNumberFn}

                                    onKeyDown={(e) => {
                                      decimalNumberFn(e);
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        // const value =
                                        //   e.currentTarget.value || 0;
                                        // handleQuantityChange(
                                        //   +values?.item_sellable_quantity || 0,
                                        //   value !== ""
                                        //     ? parseFloat(value)
                                        //     : value,
                                        //   parseFloat(
                                        //     values?.item_batch_discount_percentage ||
                                        //       0
                                        //   ) / 100,
                                        //   parseInt(
                                        //     values?.item_batch_for_qauntity || 0
                                        //   ),
                                        //   parseInt(
                                        //     values?.item_batch_for_free_qauntity ||
                                        //       0
                                        //   ),
                                        //   parseInt(
                                        //     values?.item_batch_free_quantity ||
                                        //       0
                                        //   ),
                                        //   setValues,
                                        //   values,
                                        //   parseFloat(
                                        //     values?.item_batch_scheme_discount_percentage ||
                                        //       0
                                        //   ),
                                        //   parseFloat(
                                        //     values?.item_batch_unit_price || 0
                                        //   )
                                        // );
                                        schemeQtyRef.current?.focus();
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = e.currentTarget.value || 0;
                                      handleQuantityChange(
                                        +values?.item_sellable_quantity || 0,
                                        value !== ""
                                          ? parseFloat(value)
                                          : value,
                                        parseFloat(
                                          values?.item_batch_discount_percentage ||
                                            0
                                        ) / 100,
                                        parseInt(
                                          values?.item_batch_for_qauntity || 0
                                        ),
                                        parseInt(
                                          values?.item_batch_for_free_qauntity ||
                                            0
                                        ),
                                        parseInt(
                                          values?.item_batch_free_quantity || 0
                                        ),
                                        setValues,
                                        values,
                                        parseFloat(
                                          values?.item_batch_scheme_discount_percentage ||
                                            0
                                        ),
                                        parseFloat(
                                          values?.item_batch_unit_price || 0
                                        )
                                      );
                                    }}
                                  />
                                </div>
                                <div className="flex gap-5 ">
                                  <div className="flex gap-3 w-[50%] items-center">
                                    <InputField
                                      inputRef={schemeQtyRef}
                                      defaultInputClass={defaultInputClass}
                                      labelClass={labelClass}
                                      errorBorder={errorBorder}
                                      inputBorder={inputBorder}
                                      type="number"
                                      isRequired={false}
                                      containerClass="w-[50%]"
                                      parentClass="flex flex-col gap-2"
                                      isDisabled={false}
                                      name="item_batch_for_qauntity"
                                      placeholder="Sch Qty"
                                      label="Sch Qty"
                                      labelWidth="6.5rem"
                                      errMsgContainerClass="hidden"
                                      handleChange={(e) => {}}
                                      // onKeyDown={decimalNumberFn}
                                      onKeyDown={(e) => {
                                        positiveIntegerFn(e);
                                        if (
                                          e.key === "Tab" ||
                                          e.key === "Enter"
                                        ) {
                                          e.preventDefault();
                                          // const value = +(
                                          //   e.currentTarget.value || 0
                                          // );
                                          // handleQuantityChange(
                                          //   +values?.item_sellable_quantity ||
                                          //     0,
                                          //   parseFloat(
                                          //     values?.item_batch_purchase_rate ||
                                          //       0
                                          //   ),
                                          //   parseFloat(
                                          //     values?.item_batch_discount_percentage ||
                                          //       0
                                          //   ) / 100,
                                          //   parseInt(value),
                                          //   parseInt(
                                          //     values?.item_batch_for_free_qauntity ||
                                          //       0
                                          //   ),
                                          //   parseInt(
                                          //     values?.item_batch_free_quantity ||
                                          //       0
                                          //   ),
                                          //   setValues,
                                          //   values,
                                          //   0,
                                          //   parseFloat(
                                          //     values?.item_batch_unit_price || 0
                                          //   )
                                          // );
                                          schemeFreeQtyRef.current?.focus();
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const value = +(
                                          e.currentTarget.value || 0
                                        );
                                        handleQuantityChange(
                                          +values?.item_sellable_quantity || 0,
                                          parseFloat(
                                            values?.item_batch_purchase_rate ||
                                              0
                                          ),
                                          parseFloat(
                                            values?.item_batch_discount_percentage ||
                                              0
                                          ) / 100,
                                          parseInt(value),
                                          parseInt(
                                            values?.item_batch_for_free_qauntity ||
                                              0
                                          ),
                                          parseInt(
                                            values?.item_batch_free_quantity ||
                                              0
                                          ),
                                          setValues,
                                          values,
                                          0,
                                          parseFloat(
                                            values?.item_batch_unit_price || 0
                                          )
                                        );
                                      }}
                                    />
                                    <div className="mt-5">
                                      <SquarePlus color="#6B7280" />
                                    </div>
                                    <InputField
                                      inputRef={schemeFreeQtyRef}
                                      defaultInputClass={defaultInputClass}
                                      labelClass={labelClass}
                                      errorBorder={errorBorder}
                                      inputBorder={inputBorder}
                                      type="number"
                                      isRequired={false}
                                      containerClass="w-[50%]"
                                      parentClass="flex flex-col gap-2"
                                      isDisabled={false}
                                      name="item_batch_for_free_qauntity"
                                      placeholder="Sch Free Qty"
                                      label="Sch Free Qty"
                                      labelWidth="6.5rem"
                                      errMsgContainerClass="hidden"
                                      handleChange={(e) => {}}
                                      // onKeyDown={decimalNumberFn}
                                      onKeyDown={(e) => {
                                        positiveIntegerFn(e);
                                        if (
                                          e.key === "Tab" ||
                                          e.key === "Enter"
                                        ) {
                                          e.preventDefault();
                                          // const value = +(
                                          //   e.currentTarget.value || 0
                                          // );
                                          // handleQuantityChange(
                                          //   +values?.item_sellable_quantity ||
                                          //     0,
                                          //   parseFloat(
                                          //     values?.item_batch_purchase_rate ||
                                          //       0
                                          //   ),
                                          //   parseFloat(
                                          //     values?.item_batch_discount_percentage ||
                                          //       0
                                          //   ) / 100,
                                          //   parseInt(
                                          //     values?.item_batch_for_qauntity ||
                                          //       0
                                          //   ),
                                          //   parseInt(value),
                                          //   parseInt(
                                          //     values?.item_batch_free_quantity ||
                                          //       0
                                          //   ),
                                          //   setValues,
                                          //   values,
                                          //   0,
                                          //   parseFloat(
                                          //     values?.item_batch_unit_price || 0
                                          //   )
                                          // );
                                          schemePerRef.current?.focus();
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const value = +(
                                          e.currentTarget.value || 0
                                        );
                                        handleQuantityChange(
                                          +values?.item_sellable_quantity || 0,
                                          parseFloat(
                                            values?.item_batch_purchase_rate ||
                                              0
                                          ),
                                          parseFloat(
                                            values?.item_batch_discount_percentage ||
                                              0
                                          ) / 100,
                                          parseInt(
                                            values?.item_batch_for_qauntity || 0
                                          ),
                                          parseInt(value),
                                          parseInt(
                                            values?.item_batch_free_quantity ||
                                              0
                                          ),
                                          setValues,
                                          values,
                                          0,
                                          parseFloat(
                                            values?.item_batch_unit_price || 0
                                          )
                                        );
                                      }}
                                    />
                                  </div>
                                  <InputField
                                    inputRef={schemePerRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={false}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_batch_scheme_discount_percentage"
                                    placeholder="Scheme %"
                                    label="Scheme %"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    handleChange={(e) => {}}
                                    // onKeyDown={decimalNumberFn}
                                    onKeyDown={(e) => {
                                      decimalNumberFn(e);
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        e.preventDefault();
                                        // const value = +(
                                        //   e.currentTarget.value || 0
                                        // );
                                        // handleQuantityChange(
                                        //   +values?.item_sellable_quantity || 0,
                                        //   parseFloat(
                                        //     values?.item_batch_purchase_rate ||
                                        //       0
                                        //   ),
                                        //   parseFloat(
                                        //     values?.item_batch_discount_percentage ||
                                        //       0
                                        //   ) / 100,
                                        //   0,
                                        //   0,
                                        //   parseInt(
                                        //     values?.item_batch_free_quantity ||
                                        //       0
                                        //   ),
                                        //   setValues,
                                        //   values,
                                        //   parseFloat(value),
                                        //   parseFloat(
                                        //     values?.item_batch_unit_price || 0
                                        //   )
                                        // );

                                        DisPerRef.current?.focus();
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = +(
                                        e.currentTarget.value || 0
                                      );
                                      handleQuantityChange(
                                        +values?.item_sellable_quantity || 0,
                                        parseFloat(
                                          values?.item_batch_purchase_rate || 0
                                        ),
                                        parseFloat(
                                          values?.item_batch_discount_percentage ||
                                            0
                                        ) / 100,
                                        0,
                                        0,
                                        parseInt(
                                          values?.item_batch_free_quantity || 0
                                        ),
                                        setValues,
                                        values,
                                        parseFloat(value),
                                        parseFloat(
                                          values?.item_batch_unit_price || 0
                                        )
                                      );
                                    }}
                                  />
                                </div>

                                <div className="flex gap-5">
                                  <InputField
                                    maxValue={100}
                                    inputRef={DisPerRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={false}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_batch_discount_percentage"
                                    placeholder="Discount %"
                                    label="Discount %"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onKeyDown={(e) => {
                                      decimalNumberFn(e);
                                      if (
                                        e.key === "Tab" ||
                                        e.key === "Enter"
                                      ) {
                                        // e.preventDefault();
                                        // const value =
                                        //   e.currentTarget.value || 0;
                                        // handleQuantityChange(
                                        //   +values?.item_sellable_quantity || 0,
                                        //   parseFloat(
                                        //     values?.item_batch_purchase_rate ||
                                        //       0
                                        //   ),
                                        //   parseFloat(value) / 100,
                                        //   parseInt(
                                        //     values?.item_batch_for_qauntity || 0
                                        //   ),
                                        //   parseInt(
                                        //     values?.item_batch_for_free_qauntity ||
                                        //       0
                                        //   ),
                                        //   parseInt(
                                        //     values?.item_batch_free_quantity ||
                                        //       0
                                        //   ),
                                        //   setValues,
                                        //   values,
                                        //   parseFloat(
                                        //     values?.item_batch_scheme_discount_percentage ||
                                        //       0
                                        //   ),
                                        //   parseFloat(
                                        //     values?.item_batch_unit_price || 0
                                        //   )
                                        // );
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = e.currentTarget.value || 0;
                                      handleQuantityChange(
                                        +values?.item_sellable_quantity || 0,
                                        parseFloat(
                                          values?.item_batch_purchase_rate || 0
                                        ),
                                        parseFloat(value) / 100,
                                        parseInt(
                                          values?.item_batch_for_qauntity || 0
                                        ),
                                        parseInt(
                                          values?.item_batch_for_free_qauntity ||
                                            0
                                        ),
                                        parseInt(
                                          values?.item_batch_free_quantity || 0
                                        ),
                                        setValues,
                                        values,
                                        parseFloat(
                                          values?.item_batch_scheme_discount_percentage ||
                                            0
                                        ),
                                        parseFloat(
                                          values?.item_batch_unit_price || 0
                                        )
                                      );
                                    }}
                                  />
                                  <InputField
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={false}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={true}
                                    name="item_batch_tax_percentage"
                                    placeholder="Tax %"
                                    label="Tax %"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onKeyDown={decimalNumberFn}
                                  />
                                </div>
                              </div>

                              <div className="border border-[var(--slide-panel-border)] h-fit p-3 flex flex-col   border-radius">
                                {[
                                  {
                                    label: "Final PR (Per 1 Qty)",
                                    value:
                                      values?.item_batch_final_purchase_rate ||
                                      0,
                                  },
                                  {
                                    label: "Total Base PR",
                                    value:
                                      values?.item_batch_total_base_purchase_rate ||
                                      0,
                                  },
                                  {
                                    label: "Total Discount Amount",
                                    value: values?.item_batch_discount_amount,
                                  },
                                  {
                                    label: "Taxable Amount",
                                    value:
                                      values?.item_batch_discounted_purchase_rate,
                                  },
                                  {
                                    label: "Tax Amount",
                                    value: values?.item_batch_tax_amount,
                                  },
                                  {
                                    label: "Payable or Total Amt",
                                    value:
                                      values?.item_batch_total_purchase_rate,
                                  },
                                ]?.map((sum, index) => (
                                  <div
                                    className={`flex justify-between ${
                                      index === 5
                                        ? ""
                                        : " border-b border-b-[var(--slide-panel-border)]"
                                    } p-3`}
                                  >
                                    <span className="text-[var(--payment-summary-label-fg)] text-[length:var(--payment-summary-label-fs)] font-[var(--payment-summary-label-fw)]">
                                      {sum?.label}
                                    </span>
                                    <span className="text-[var(--payment-summary-value-fg)] text-[length:var(--payment-summary-value-fs)] font-[var(--payment-summary-value-fw)]">
                                      ₹ {sum?.value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              height: `calc(100% - 61px)`,
                              minHeight: `calc(100% - 61px)`,
                            }}
                            className="px-4 pt-5 relative"
                          >
                            <div className="h-[2rem] mb-[1rem] flex justify-between items-center">
                              <span className="text-[var(--slide-panel-h-fg)] text-[length:var(--slide-panel-h-fs)]  font-[var(--slide-panel-h-fw)]">
                                Batches For {defaultSelectedItem?.item_name}
                              </span>
                              {!disabled && (
                                <AddButton
                                  type="button"
                                  icon={
                                    <PlusIcon
                                      size={18}
                                      fill="var(--primary-button-fg-color)"
                                    />
                                  }
                                  className="border-radius hover:bg-[var(--primary-button-hover-color)] flex justify-center gap-[8px] h-[36px] px-[12px]  items-center   whitespace-nowrap   transition duration-300 ease-in-out   text-[length:--primary-button-font-size] font-[var(--primary-button-font-weight)] bg-[var(--primary-button-bg-color)]  cursor-pointer text-[var(--primary-button-fg-color)]"
                                  onclkFn={() => {
                                    setNewBatch(true);
                                    const preservedBatches =
                                      values.item_batches;
                                    const { item_batches: _, ...restInitials } =
                                      initialValues;
                                    setValues({
                                      ...restInitials,
                                      item_batches: preservedBatches,
                                    });
                                    setOpenBatchIndex(null);
                                    batchRef.current?.focus();
                                  }}
                                  text={"Add New Batch"}
                                />
                              )}
                            </div>
                            <div
                              className="overflow-y-auto"
                              style={{
                                height: `calc(100% - 8rem)`,
                                minHeight: `calc(100% - 8rem)`,
                              }}
                            >
                              {batches?.map((batch, batchIndex) => {
                                const isOpen = openBatchIndex === batchIndex;

                                return (
                                  <div
                                    key={batchIndex}
                                    className="mt-4 border border-[var(--popover-border-color)] rounded-[8px] bg-[var(--slide-panel-accordian-bg)] overflow-hidden"
                                  >
                                    {/* Header */}
                                    <div
                                      className={`h-[44px] cursor-pointer flex justify-between px-3 ${
                                        isOpen
                                          ? "border-b border-b-[var(--popover-border-color)]"
                                          : ""
                                      }`}
                                      onClick={() => toggleBatch(batchIndex)}
                                    >
                                      <div className="flex h-full items-center gap-2">
                                        <Package
                                          size={"20px"}
                                          color="var(--slide-panel-label)"
                                        />
                                        <span className="text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                          {batch.item_batch_number}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                        MRP : ₹{batch.item_batch_unit_price}
                                        <ChevronDownIcon
                                          className={`transition-transform duration-200 ${
                                            isOpen ? "rotate-180" : ""
                                          }`}
                                        />
                                      </div>
                                    </div>
                                    {/* Body */}
                                    {isOpen && (
                                      <div className="p-3 bg-[var(--slide-panel-accordian-open-bg)]">
                                        <div className="flex flex-wrap">
                                          {" "}
                                          {[
                                            {
                                              label: "To Bin",
                                              value:
                                                batch?.to_bin_location || 0,
                                            },
                                            {
                                              label: "Purchase Qty",
                                              value:
                                                batch?.item_sellable_quantity ||
                                                0,
                                            },
                                            {
                                              label: "Free Qty",
                                              value:
                                                batch?.item_batch_free_quantity,
                                            },
                                            {
                                              label: "MRP (Per 1 Qty)",
                                              value:
                                                batch?.item_batch_unit_price,
                                            },
                                            {
                                              label: "Base PR (Per 1 Qty)",
                                              value:
                                                batch?.item_batch_purchase_rate,
                                            },
                                            {
                                              label: "Discount %",
                                              value:
                                                batch?.item_batch_discount_percentage,
                                            },
                                            {
                                              label: "Tax %",
                                              value:
                                                batch?.item_batch_tax_percentage,
                                            },
                                          ]?.map((bat, index) => (
                                            <div
                                              className={`flex flex-col gap-1 w-[50%]   mt-[1rem]`}
                                            >
                                              <div className="text-[var(--slide-panel-label)] text-[length:var(--slide-panel-label-fs)] font-[var(--slide-label-value-fw)]">
                                                {bat?.label}
                                              </div>
                                              <div className="text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)] text-wrap">
                                                {bat?.value}
                                              </div>
                                            </div>
                                          ))}
                                        </div>

                                        <div className="border border-[var(--slide-panel-border)] h-fit p-3 flex flex-col   border-radius mt-[1rem]">
                                          {[
                                            {
                                              label: "Final PR (Per 1 Qty)",
                                              value:
                                                batch?.item_batch_final_purchase_rate ||
                                                0,
                                            },
                                            {
                                              label: "Total Base PR",
                                              value:
                                                batch?.item_batch_total_base_purchase_rate ||
                                                0,
                                            },
                                            {
                                              label: "Total Discount Amount",
                                              value:
                                                batch?.item_batch_discount_amount,
                                            },
                                            {
                                              label: "Taxable Amount",
                                              value:
                                                batch?.item_batch_discounted_purchase_rate,
                                            },
                                            {
                                              label: "Tax Amount",
                                              value:
                                                batch?.item_batch_tax_amount,
                                            },
                                            {
                                              label: "Payable or Total Amt",
                                              value:
                                                batch?.item_batch_total_purchase_rate,
                                            },
                                          ]?.map((sum, index) => (
                                            <div
                                              className={`flex justify-between 
                                                 p-2 ${
                                                   index === 5
                                                     ? "border-t border-t-[var(--slide-panel-border)]"
                                                     : ""
                                                 }`}
                                            >
                                              <span
                                                className={`text-[var(--payment-summary-label-fg)] text-[length:var(--payment-summary-label-fs)] font-[var(--payment-summary-label-fw)]`}
                                              >
                                                {sum?.label}
                                              </span>
                                              <span className="text-[var(--payment-summary-value-fg)] text-[length:var(--payment-summary-value-fs)] font-[var(--payment-summary-value-fw)]">
                                                ₹ {sum?.value}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                        {!disabled && (
                                          <div className="flex justify-between mt-[1rem]">
                                            <AddButton
                                              onclkFn={() => {
                                                handleDeleteBatch(batchIndex);
                                              }}
                                              prefixIcon={false}
                                              text={"Delete"}
                                              className="border rounded-[6px]  border-[var(--status-badge-red-border)] text-[length:--status-badge-special-fs] font-[var(--status-badge-fw)]     text-[var(--status-badge-red-fg)] bg-[var(--status-badge-red-bg)] px-3 h-[36px]"
                                              type="button"
                                            />
                                            <AddButton
                                              onclkFn={() => {
                                                setValues(batch);
                                                setNewBatch(true);
                                              }}
                                              prefixIcon={false}
                                              text={"Edit Batch"}
                                              className="border rounded-[6px]  border-[var(--status-badge-blue-border)] text-[length:--status-badge-special-fs] font-[var(--status-badge-fw)]     text-[var(--status-badge-blue-fg)] bg-[var(--status-badge-blue-bg)] px-3 h-[36px]"
                                              type="button"
                                            />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="h-[5rem]  absolute bottom-0 bg-white  flex justify-between items-center px-3 w-full">
                          <div></div>
                          <AddButton
                            onclkFn={() => {
                              if (newBatch) {
                                handleShowErrorToast("", errors, values);
                                handleQuantityChange(
                                  +values?.item_sellable_quantity || 0,
                                  parseFloat(
                                    values?.item_batch_purchase_rate || 0
                                  ),
                                  parseFloat(
                                    values?.item_batch_discount_percentage || 0
                                  ) / 100,
                                  parseInt(
                                    values?.item_batch_for_qauntity || 0
                                  ),
                                  parseInt(
                                    values?.item_batch_for_free_qauntity || 0
                                  ),
                                  parseInt(
                                    values?.item_batch_free_quantity || 0
                                  ),
                                  setValues,
                                  values,
                                  parseFloat(
                                    values?.item_batch_scheme_discount_percentage ||
                                      0
                                  ),
                                  parseFloat(values?.item_batch_unit_price || 0)
                                );
                                handleSubmit(); // Submit after handleQuantityChange completes
                              } else {
                                setOpen(false);
                                if (searchRef && searchRef.current) {
                                  setTimeout(() => {
                                    searchRef.current.focus();
                                  }, 0);
                                }
                              }
                            }}
                            prefixIcon={false}
                            text={
                              newBatch && openBatchIndex
                                ? "Update Batch"
                                : newBatch
                                ? "Add Batch"
                                : "Done"
                            }
                            className="
    flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
    bg-[var(--primary-button-bg-color)] cursor-pointer
    text-[var(--primary-button-fg-color)]
    border border-[var(--primary-button-bg-color)]
    hover:bg-[var(--primary-button-hover-color)]
    text-[length:var(--primary-button-font-size)] 
    font-[var(--primary-button-font-weight)]
  "
                            type="button"
                          />
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </Dialog.Panel>
            </Transition.Child>
          </div>
          <LoadingOverlay isLoading={loading} />
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PurchaseBatchesPanel;
